/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { getHeaders, handleResponse } from '@opengeoweb/api';
import type { Credentials } from '@opengeoweb/authentication';
import { sessionStorageProvider } from '@opengeoweb/shared';
import type {
  TAC,
  Taf,
  TafConfig,
  TafFromBackend,
  TafFromFrontEnd,
  ForecastType,
} from '../../types';

const config = sessionStorageProvider.getConfig();

const tafBaseUrl = config.GW_TAF_BASE_URL || '';

export const getTafList = async (
  auth: Credentials,
  forecastType: ForecastType = 'TAF',
): Promise<TafFromBackend[]> =>
  fetch(`${tafBaseUrl}/taflist?forecastType=${forecastType}`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<TafFromBackend[]>;

export const postTaf = async (
  auth: Credentials,
  data: TafFromFrontEnd,
): Promise<Response> =>
  fetch(`${tafBaseUrl}/taf`, {
    method: 'POST',
    headers: getHeaders(auth),
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<Response>;

export const patchTaf = async (
  auth: Credentials,
  data: TafFromFrontEnd,
  force?: boolean,
): Promise<Response> =>
  fetch(`${tafBaseUrl}/taf?force=${force ? 'true' : 'false'}`, {
    method: 'PATCH',
    headers: getHeaders(auth),
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<Response>;

export const getTAC = async (
  auth: Credentials,
  data: Taf,
  forecastType: ForecastType = 'TAF',
): Promise<TAC> =>
  fetch(`${tafBaseUrl}/taf2tac?forecastType=${forecastType}`, {
    method: 'POST',
    headers: getHeaders(auth),
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<TAC>;

export const getTafConfig = async (
  auth: Credentials,
  forecastType: ForecastType = 'TAF',
): Promise<TafConfig> =>
  fetch(`${tafBaseUrl}/taf-config?forecastType=${forecastType}`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<TafConfig>;
